import { useState } from 'react';
import { LifeBuoy, ChevronDown, Plus, Check, ClipboardList, MessageCircleQuestion, Gavel, Quote } from 'lucide-react';
import { Button } from '../ui/button';

const Titre = ({ Icone, children }) => (
  <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Icone className="h-3.5 w-3.5 text-[#6FA22E]" /> {children}</p>
);

const Liste = ({ Icone, titre, items, testId }) => (
  <div data-testid={testId}>
    <Titre Icone={Icone}>{titre}</Titre>
    <ul className="mt-2 space-y-1.5">
      {items.map((t) => <li key={t} className="flex gap-2 text-sm text-slate-700"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#8DC63F]" />{t}</li>)}
    </ul>
  </div>
);

const DecisionsProposees = ({ items, decisions, onDecision, i }) => {
  const prises = decisions.map((d) => d.texte.trim());
  return (
    <div data-testid={`cp-aide-decisions-${i}`}>
      <Titre Icone={Gavel}>Décisions possibles (un clic pour l'ajouter)</Titre>
      <ul className="mt-2 space-y-1.5">
        {items.map((t, k) => {
          const ok = prises.includes(t);
          return (
            <li key={t}>
              <button type="button" disabled={ok} onClick={() => onDecision(t)} data-testid={`cp-aide-decision-${i}-${k}`}
                className="flex w-full items-start gap-2 rounded-lg bg-white px-3 py-2 text-left text-sm text-slate-700 shadow-sm transition-colors hover:bg-[#8DC63F]/10 disabled:cursor-default disabled:opacity-50 disabled:hover:bg-white">
                {ok ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <Plus className="mt-0.5 h-4 w-4 shrink-0 text-[#6FA22E]" />}{t}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const Formulation = ({ texte, dejaInseree, onInserer, i }) => (
  <div data-testid={`cp-aide-formulation-${i}`}>
    <Titre Icone={Quote}>Formulation type pour le procès-verbal</Titre>
    <p className="mt-2 rounded-lg border-l-4 border-[#8DC63F] bg-white px-3 py-2 text-sm italic text-slate-600">{texte}</p>
    <Button size="sm" variant="outline" disabled={dejaInseree} onClick={onInserer} className="mt-2 bg-white" data-testid={`cp-aide-inserer-${i}`}>
      {dejaInseree ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Plus className="mr-1.5 h-3.5 w-3.5" />} Insérer dans « Ce qui a été présenté »
    </Button>
  </div>
);

export const AideSeance = ({ aide, i, p, onChange }) => {
  const [ouvert, setOuvert] = useState(false);
  if (!aide) return null;
  const ajouterDecision = (texte) => onChange({ ...p, decisions: [...p.decisions, { texte, responsable: '', echeance: '', preuve: '' }] });
  const inserer = () => onChange({ ...p, presente: p.presente.trim() ? `${p.presente.trim()}\n\n${aide.formulation}` : aide.formulation });
  return (
    <div className="mt-4 rounded-xl border border-[#8DC63F]/40 bg-[#8DC63F]/5" data-testid={`cp-aide-${i}`}>
      <button type="button" onClick={() => setOuvert(!ouvert)} aria-expanded={ouvert} data-testid={`cp-aide-toggle-${i}`}
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-[#0F172A]">
        <LifeBuoy className="h-4 w-4 shrink-0 text-[#6FA22E]" /> Aide en séance
        <span className="hidden font-normal text-slate-500 sm:inline">· éléments à présenter, questions, décisions possibles</span>
        <ChevronDown className={`ml-auto h-4 w-4 shrink-0 transition-transform duration-200 ${ouvert ? 'rotate-180' : ''}`} />
      </button>
      {ouvert && (
        <div className="grid gap-6 border-t border-[#8DC63F]/30 px-4 py-5 lg:grid-cols-2" data-testid={`cp-aide-contenu-${i}`}>
          <Liste Icone={ClipboardList} titre="Éléments à présenter" items={aide.elements} testId={`cp-aide-elements-${i}`} />
          <Liste Icone={MessageCircleQuestion} titre="Questions à poser aux membres" items={aide.questions} testId={`cp-aide-questions-${i}`} />
          <DecisionsProposees items={aide.decisions} decisions={p.decisions} onDecision={ajouterDecision} i={i} />
          <Formulation texte={aide.formulation} dejaInseree={p.presente.includes(aide.formulation)} onInserer={inserer} i={i} />
        </div>
      )}
    </div>
  );
};
